import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import MainCard from "../../../components/MainCard";
import { getAllFxRatesApi, recordTodayRatesApi } from "../../../components/api/FxRateApiService";

/* ─────────────────────────── helpers ─────────────────────────── */

const fmt4 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const fmt2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtRate(val, large = false) {
  if (val == null) return "—";
  return large ? fmt2.format(Number(val)) : fmt4.format(Number(val));
}

function deriveEurUsd(usdEur) {
  const n = Number(usdEur);
  if (!Number.isFinite(n) || n === 0) return null;
  return 1 / n;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

/* ─────────────────────────── component ───────────────────────── */

const COLUMNS = [
  { id: "rateDate", label: "Date",    align: "left"  },
  { id: "usdSgd",  label: "USD/SGD", align: "right" },
  { id: "usdEur",  label: "USD/EUR", align: "right" },
  { id: "eurUsd",  label: "EUR/USD", align: "right" },
  { id: "usdBrl",  label: "USD/BRL", align: "right" },
  { id: "usdIdr",  label: "USD/IDR", align: "right" },
  { id: "usdVnd",  label: "USD/VND", align: "right" },
];

export default function FxRateReportView() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [order, setOrder]       = useState("desc");
  const [orderBy, setOrderBy]   = useState("rateDate");
  const [page, setPage]         = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");

  /* ── fetch ── */
  const fetchRates = async () => {
    try {
      setLoading(true);
      const { data } = await getAllFxRatesApi();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load FX rate records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, []);

  /* ── record today ── */
  const handleRecordToday = async () => {
    try {
      setSaving(true);
      await recordTodayRatesApi();
      toast.success("Today's rates recorded.");
      fetchRates();
    } catch (e) {
      const msg = e?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Failed to record today's rates.");
    } finally {
      setSaving(false);
    }
  };

  /* ── sort ── */
  const handleSort = (col) => {
    setOrder(orderBy === col && order === "asc" ? "desc" : "asc");
    setOrderBy(col);
    setPage(0);
  };

  /* ── filter + sort ── */
  const filtered = useMemo(() => {
    let data = rows.map((r) => ({ ...r, eurUsd: deriveEurUsd(r.usdEur) }));
    if (fromDate) data = data.filter((r) => r.rateDate >= fromDate);
    if (toDate)   data = data.filter((r) => r.rateDate <= toDate);
    return data.sort(getComparator(order, orderBy));
  }, [rows, fromDate, toDate, order, orderBy]);

  /* ── export ── */
  const handleExport = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("FX Rates");
    ws.columns = [
      { header: "Date",    key: "rateDate", width: 14 },
      { header: "USD/SGD", key: "usdSgd",  width: 12 },
      { header: "USD/EUR", key: "usdEur",  width: 12 },
      { header: "EUR/USD", key: "eurUsd",  width: 12 },
      { header: "USD/BRL", key: "usdBrl",  width: 12 },
      { header: "USD/IDR", key: "usdIdr",  width: 14 },
      { header: "USD/VND", key: "usdVnd",  width: 14 },
    ];
    ws.getRow(1).font = { bold: true };
    filtered.forEach((r) =>
      ws.addRow({
        rateDate: r.rateDate,
        usdSgd:  r.usdSgd != null ? Number(r.usdSgd) : "",
        usdEur:  r.usdEur != null ? Number(r.usdEur) : "",
        eurUsd:  deriveEurUsd(r.usdEur) ?? "",
        usdBrl:  r.usdBrl != null ? Number(r.usdBrl) : "",
        usdIdr:  r.usdIdr != null ? Number(r.usdIdr) : "",
        usdVnd:  r.usdVnd != null ? Number(r.usdVnd) : "",
      })
    );
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "fx_rates.xlsx");
  };

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  /* ── render ── */
  return (
    <MainCard
      title="FX Rate History"
      secondary={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleRecordToday}
            disabled={saving}
          >
            Record Today
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={filtered.length === 0}
          >
            Export Excel
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchRates}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      }
    >
      {/* ── filters ── */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(0); }}
        />
        {(fromDate || toDate) && (
          <Button size="small" onClick={() => { setFromDate(""); setToDate(""); setPage(0); }}>
            Clear
          </Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto !important" }}>
          {filtered.length} record{filtered.length !== 1 ? "s" : ""}
        </Typography>
      </Stack>

      <Divider />

      {/* ── table ── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.align} sortDirection={orderBy === col.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                    {orderBy === col.id && (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No records found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.rateDate}</TableCell>
                  <TableCell align="right">{fmtRate(row.usdSgd)}</TableCell>
                  <TableCell align="right">{fmtRate(row.usdEur)}</TableCell>
                  <TableCell align="right">{fmtRate(deriveEurUsd(row.usdEur))}</TableCell>
                  <TableCell align="right">{fmtRate(row.usdBrl)}</TableCell>
                  <TableCell align="right">{fmtRate(row.usdIdr, true)}</TableCell>
                  <TableCell align="right">{fmtRate(row.usdVnd, true)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </MainCard>
  );
}
